import sortBy from "lodash/sortBy";

import { MemberSetModel } from "@app/members";
import CreateProjectOverrideMemberSet from "@app/projects/createProjectOverrideMemberSet";
import { TemplateSpecialtyGroup } from "@app/templates";
import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { numberSort } from "@app/shared/waffles/exports";
import CreateProject from "@app/projects/create/createProject";
import { SpecialtyOverrides } from "@app/projects/create/interfaces";
import { FC, Fragment, JSX, useEffect, useRef, useState } from "react";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";


interface MembershipBindingProps {
  alternateMemberships: MemberSetModel[];
  canEdit: boolean;
  createProjectModel: CreateProject;
  defaultOpen?: boolean;
  excludedSpecialtyGroupCodes: string[];
  handlePrimaryMembershipBindingChange: (memberPop: number, code: string) => void;
  handleSecondaryMembershipBindingChange: (memberPop: number, code: string) => void;
  projectMemberPopulations: MemberSetModel[];
  setPrimarySpecialtyOverride: (memberPop: number, code: string) => void;
  setSecondarySpecialtyOverride: (memberPop: number, code: string) => void;
  specialtyGroups: TemplateSpecialtyGroup[];
  specialtyOverrides: SpecialtyOverrides[];
  uniqueKey: string;
}

interface TableData {
  code: string;
  key: string;
  name: string;
  specialtyGroupPrimaryMembership: JSX.Element;
  specialtyGroupSecondaryMembership: JSX.Element;
}

const sortOrder: Array<keyof TemplateSpecialtyGroup> = ["displayOrder"];
const basePanelTitle: string = "Member Population Binding";

const sortArrayAfterFirstItem = (arr: MemberSetModel[]): MemberSetModel[] => {
  let newArr = arr;
  if (newArr.length === 0) {
    return newArr;
  }
  const firstItem = newArr.shift();
  newArr = newArr.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1);
  newArr.unshift(firstItem);
  return newArr;
}

const customSort = (records: TableData[], column: keyof TableData, direction: SortDirections) => {
  const data = [...records];
  if (direction === SortDirections.None) {
    return data;
  }
  const multiplier = direction === SortDirections.Asc ? 1 : -1;
  switch (column) {
    case "code":
      return numberSort<TableData>(records, column, multiplier);
    case "name":
      return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
    case "specialtyGroupPrimaryMembership":
      return data.sort((a, b) => (a.specialtyGroupPrimaryMembership.props.value > b.specialtyGroupPrimaryMembership.props.value ? 1 : -1) * multiplier);
    case "specialtyGroupSecondaryMembership":
      return data.sort((a, b) => (a.specialtyGroupSecondaryMembership.props.value > b.specialtyGroupSecondaryMembership.props.value ? 1 : -1) * multiplier);
    default:
      return data;
  }
}

const MembershipBindingTable: FC<MembershipBindingProps> = (props: MembershipBindingProps) => {
  const [massBindings, setMassBindings] = useState<TemplateSpecialtyGroup[]>([]);
  const [showBinding, setShowBinding] = useState(false);
  const [uniqueKey, setUniqueKey] = useState(props.uniqueKey);
  const sortedRecords = useRef(props.specialtyGroups && sortBy(props.specialtyGroups, sortOrder) || props.specialtyGroups).current;

  useEffect(() => {
    if (props.uniqueKey !== uniqueKey)
      setUniqueKey(props.uniqueKey);
  }, [props.uniqueKey, uniqueKey]);

  const selectedKeys: string[] = massBindings.map((sr: TemplateSpecialtyGroup) => sr.code);

  if (!sortedRecords) {
    return null;
  }

  const renderMassBinding = () => {
    setShowBinding((prevShowBinding) => !prevShowBinding);
  }

  const handlerPrimary = (e: React.ChangeEvent<HTMLSelectElement>, code: string) => {
    let memberPop = +(e.target.value);
    if (memberPop === 0) {
      memberPop = null;
    }
    props.setPrimarySpecialtyOverride(memberPop, code);
    props.handlePrimaryMembershipBindingChange(memberPop, code);
  }

  const handlerSecondary = (e: React.ChangeEvent<HTMLSelectElement>, code: string) => {
    let memberPop = +(e.target.value);
    if (memberPop === 0) {
      memberPop = null;
    }
    props.setSecondarySpecialtyOverride(memberPop, code);
    props.handleSecondaryMembershipBindingChange(memberPop, code);
  }

  const handlePrimaryMemberPopulationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let memberPop = +(e.target.value);
    if (memberPop === 0) {
      memberPop = null;
    }
    massBindings.forEach((x) => {
      props.handlePrimaryMembershipBindingChange(memberPop, x.code);
      props.setPrimarySpecialtyOverride(memberPop, x.code);
    });
  }

  const handleSecondaryMemberPopulationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let memberPop = +(e.target.value);
    if (memberPop === 0) {
      memberPop = null;
    }
    massBindings.forEach((x) => {
      props.handleSecondaryMembershipBindingChange(memberPop, x.code);
      props.setSecondarySpecialtyOverride(memberPop, x.code);
    });
  }

  const handleWaffleSelectionChange = (selectedCodes: string[]) => {
    const selectedProps = sortedRecords.filter((sr) => {
      return selectedCodes.includes(sr.code);
    });
    setMassBindings(selectedProps);
    setUniqueKey(`reset${(Math.random() * 100).toFixed(0)}`);
  }

  const disableSecondary = () => {
    return props.createProjectModel.memberSetId2 === null;
  }

  const disableMassButton = () => {
    return massBindings.length === 0;
  }

  const getAlternateDisplay = (memberPop: number, memberPopName: string) => {
    const updates: CreateProjectOverrideMemberSet[] = props.createProjectModel.createProjectOverrideMemberSets;
    let alias = "";
    updates.forEach((x) => {
      if (x.memberSetId === memberPop) {
        if (x.alias !== "") {
          alias = x.alias;
        } else {
          alias = memberPopName;
        }
      }
    });
    return alias;
  }

  const records: TableData[] = sortedRecords.map((rec) => {
    return {
      code: rec.code,
      name: rec.name,
      specialtyGroupPrimaryMembership: (() => {
        if (props.specialtyOverrides.length > 0) {
          return (
            <QESSelect
              label="Primary Membership"
              hideLabel={true}
              testId="membership-binding__primary-membership-select"
              className="form-control"
              onChange={(e) => handlerPrimary(e, rec.code)}
              value={props.specialtyOverrides.filter((x) => x.code === rec.code)[0].primaryMembership ? props.specialtyOverrides.filter((x) => x.code === rec.code)[0].primaryMembership : ""}
            >
              <option key="0" value="0">Default Primary Member Population</option>
              {(sortArrayAfterFirstItem(props.alternateMemberships) as MemberSetModel[]).map((option) => (
                option.hasActivatedVersions ?
                  <option key={option.memberSetId} value={option.memberSetId}>{getAlternateDisplay(option.memberSetId, option.resource.name)}</option> :
                  <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
              ))}
            </QESSelect>
          );
        }
      })(),
      specialtyGroupSecondaryMembership: (() => {
        if (props.specialtyOverrides.length > 0) {
          return (
            <QESSelect
              label="Secondary Membership"
              hideLabel={true}
              testId="membership-binding__secondary-membership-select"
              className="form-control"
              disabled={disableSecondary()}
              onChange={(e) => handlerSecondary(e, rec.code)}
              value={props.specialtyOverrides.filter((x) => x.code === rec.code)[0].secondaryMembership ? props.specialtyOverrides.filter((x) => x.code === rec.code)[0].secondaryMembership : ""}
            >
              <option key="0" value="0">Default Secondary Member Population</option>
              {
                (sortArrayAfterFirstItem(props.alternateMemberships) as MemberSetModel[]).map((option) => (
                  option.hasActivatedVersions ?
                    <option key={option.memberSetId} value={option.memberSetId}>{getAlternateDisplay(option.memberSetId, option.resource.name)}</option> :
                    <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                ))
              }

            </QESSelect>
          );
        }
      })(),
      [wafflesKeyColumnName]: rec.code,
    };
  });

  const dualColumnConfig = {
    code: {
      displayText: "Code",
      testId: "code",
    },
    name: {
      displayText: "Description",
      testId: "description",
    },
    specialtyGroupPrimaryMembership: {
      displayText: "Primary Member Population",
      testId: "member-population-population",
    },
    specialtyGroupSecondaryMembership: {
      displayText: "Secondary Member Population",
      testId: "secondary-member-population",
    },
  };

  return (
    <div className="form-group">
      <div>
        <AccordionContainer>
          <Accordion
            title={basePanelTitle}
            defaultOpen={!!props.defaultOpen}
            testId={`membership-binding-table__${basePanelTitle}-accordion`}
          >
            <Fragment>
              <div className="col-12">
                <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
                  <p className="col-auto">Check the specialty groups to bind multiple...</p>
                  <QESButton qesTestId={`bind_memberships`} onClick={renderMassBinding} disabled={disableMassButton()} className="col-auto">Bind Memberships</QESButton>
                </header>
                {showBinding && (
                  <div>
                    <div className="form-group row col-5" style={{ marginTop: "1rem" }}>
                      <QESSelect
                        label="Alternate Primary Membership"
                        hideLabel={true}
                        testId="membership-binding__alternate-primary"
                        className="form-control"
                        onChange={handlePrimaryMemberPopulationChange}
                      >
                        <option key="0" value="0">Default Primary Member Population</option>
                        {(sortArrayAfterFirstItem(props.alternateMemberships) as MemberSetModel[]).map((option) => (
                          option.hasActivatedVersions ?
                            <option key={option.memberSetId} value={option.memberSetId}>{getAlternateDisplay(option.memberSetId, option.resource.name)}</option> :
                            <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                        ))}
                      </QESSelect>
                    </div>
                    <div className="form-group row col-5">
                      <QESSelect
                        label="Alernate Secondary Membership"
                        hideLabel={true}
                        testId="membership-binding__alternate-secondary"
                        className="form-control"
                        disabled={disableSecondary()}
                        onChange={handleSecondaryMemberPopulationChange}
                      >
                        <option key="0" value="0">Default Secondary Member Population</option>
                        {(sortArrayAfterFirstItem(props.alternateMemberships) as MemberSetModel[]).map((option) => (
                          option.hasActivatedVersions ?
                            <option key={option.memberSetId} value={option.memberSetId}>{getAlternateDisplay(option.memberSetId, option.resource.name)}</option> :
                            <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                        ))}
                      </QESSelect>
                    </div>
                  </div>
                )}
                <Waffles<TableData>
                  columns={dualColumnConfig}
                  customSort={customSort}
                  data={records.filter((e) => !props.excludedSpecialtyGroupCodes.includes(e.code))}
                  disableProcessingOverlay={true}
                  fullWidth={true}
                  handleCheckboxChange={handleWaffleSelectionChange}
                  selectedKeys={selectedKeys}
                  showCheckboxes={props.canEdit}
                  testId="membership-bindings-table"
                />
              </div>
            </Fragment>
          </Accordion>
        </AccordionContainer>
      </div>
    </div>
  );
}

export default MembershipBindingTable;
